import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../lib/firebase';
import { 
  onAuthStateChanged, 
  User, 
  GoogleAuthProvider, 
  OAuthProvider,
  signInWithPopup, 
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
  UserCredential
} from 'firebase/auth';
import { doc, getDoc, setDoc, addDoc, query, collection, where, getDocs, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';

interface AuthContextType {
  user: User | null;
  userData: any | null;
  isAdmin: boolean;
  isStaff: boolean;
  staffInfo: any | null;
  loading: boolean;
  signInWithGoogle: () => Promise<UserCredential | void>;
  signInWithApple: () => Promise<UserCredential | void>;
  signInWithEmail: (email: string, pass: string) => Promise<void>;
  signUpWithEmail: (email: string, pass: string, name: string) => Promise<void>;
  setupRecaptcha: (containerId: string) => void;
  signInWithPhone: (phoneNumber: string) => Promise<ConfirmationResult>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userData: null,
  isAdmin: false,
  isStaff: false,
  staffInfo: null,
  loading: true,
  signInWithGoogle: async () => {},
  signInWithApple: async () => {},
  signInWithEmail: async () => {},
  signUpWithEmail: async () => {},
  setupRecaptcha: () => {},
  signInWithPhone: async () => ({} as ConfirmationResult),
  logout: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<any | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [staffInfo, setStaffInfo] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<RecaptchaVerifier | null>(null);

  useEffect(() => {
    // Ensure persistence is local (default but explicit for reliability)
    setPersistence(auth, browserLocalPersistence).catch(err => {
      console.error("Auth persistence error:", err);
    });
    // Set language code for Recaptcha and Emails
    auth.languageCode = 'en';
  }, []);

  useEffect(() => {
    let userDocUnsubscribe: (() => void) | null = null;
    
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        // Run background checks without blocking onAuthStateChanged state update
        const initializeUser = async () => {
          try {
            const userRef = doc(db, 'users', currentUser.uid);
            
            // Check for existing user doc first
            const userSnap = await getDoc(userRef);
            if (!userSnap.exists()) {
              const initialData = {
                userId: currentUser.uid,
                email: currentUser.email || '',
                phoneNumber: currentUser.phoneNumber || '',
                displayName: currentUser.displayName || '',
                savedAddresses: [],
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              };
              await setDoc(userRef, initialData).catch(console.error);
            }

            // Real-time listener for current user data
            userDocUnsubscribe = onSnapshot(userRef, (snapshot) => {
              if (snapshot.exists()) {
                setUserData(snapshot.data({ serverTimestamps: 'estimate' }));
              }
            });

            // Admin Logic
            const emailLower = currentUser.email?.toLowerCase().trim() || '';
            const isAuthorizedAdminEmail = emailLower === 'manjunathas788@gmail.com' || emailLower.startsWith('admin@');

            let adminDocExists = false;
            try {
              const adminSnap = await getDoc(doc(db, 'admins', currentUser.uid));
              adminDocExists = adminSnap.exists();
            } catch (err) {
              console.warn("Admin doc read check warning:", err);
            }

            if (adminDocExists || isAuthorizedAdminEmail) {
              setIsAdmin(true);
              if (isAuthorizedAdminEmail && !adminDocExists) {
                setDoc(doc(db, 'admins', currentUser.uid), {
                  email: currentUser.email,
                  role: 'superadmin',
                  createdAt: serverTimestamp()
                }, { merge: true }).catch(err => console.error("Admin bootstrap failed:", err));
              }
            } else {
              setIsAdmin(false);
            }

            // Real-time Staff Logic
            const qStaff = query(collection(db, 'staff'), where('email', '==', currentUser.email));
            const unsubStaff = onSnapshot(qStaff, async (snapshot) => {
              if (!snapshot.empty) {
                const staffDoc = snapshot.docs[0];
                const sData = staffDoc.data({ serverTimestamps: 'estimate' });
                const resolvedStaffInfo = { id: staffDoc.id, ...sData };
                setStaffInfo(resolvedStaffInfo);
                setIsStaff(true);

                // Sync staff document with currentUser.uid as ID so security rules and lookups succeed
                setDoc(doc(db, 'staff', currentUser.uid), {
                  userId: currentUser.uid,
                  email: currentUser.email,
                  name: sData.name || currentUser.displayName || 'Staff Operator',
                  facilityCenterId: sData.facilityCenterId || '',
                  role: sData.role || 'staff',
                  updatedAt: serverTimestamp()
                }, { merge: true }).catch(console.error);
              } else if (adminDocExists || isAuthorizedAdminEmail) {
                // Admin has full operational access to facility operations
                setIsStaff(true);
                try {
                  const fcSnap = await getDocs(collection(db, 'facilityCenters'));
                  let primaryCenterId = '';
                  if (!fcSnap.empty) {
                    const active = fcSnap.docs.find(d => d.data().isActive);
                    primaryCenterId = active ? active.id : fcSnap.docs[0].id;
                  } else {
                    // Create primary facility center if none exists yet
                    const newCenterRef = await addDoc(collection(db, 'facilityCenters'), {
                      name: 'Primary Fulfillment Hub',
                      code: '560001',
                      pincode: '560001',
                      pincodes: ['560001', '560002', '560003', '560004'],
                      isActive: true,
                      createdAt: serverTimestamp()
                    });
                    primaryCenterId = newCenterRef.id;
                  }

                  const adminStaffData = {
                    id: currentUser.uid,
                    userId: currentUser.uid,
                    email: currentUser.email,
                    name: currentUser.displayName || 'Operations Lead',
                    facilityCenterId: primaryCenterId,
                    role: 'manager'
                  };
                  setStaffInfo(adminStaffData);
                  setDoc(doc(db, 'staff', currentUser.uid), {
                    ...adminStaffData,
                    updatedAt: serverTimestamp()
                  }, { merge: true }).catch(console.error);
                } catch (fcErr) {
                  console.warn("Facility hub lookup note:", fcErr);
                }
              } else {
                setIsStaff(false);
                setStaffInfo(null);
              }
            });

            // Cleanup for staff unsub
            const originalCleanup = userDocUnsubscribe;
            userDocUnsubscribe = () => {
              if (originalCleanup) originalCleanup();
              unsubStaff();
            };
          } catch (error) {
            console.error("Background auth initialization error", error);
          }
        };

        initializeUser();
      } else {
        setIsAdmin(false);
        setIsStaff(false);
        setStaffInfo(null);
        setUserData(null);
        if (userDocUnsubscribe) {
          userDocUnsubscribe();
          userDocUnsubscribe = null;
        }
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (userDocUnsubscribe) userDocUnsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    try {
      return await signInWithPopup(auth, provider);
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') {
        // User closed the popup, this is a normal action, not an error to log
        throw error;
      }
      console.error("Google Sign-In Error:", error);
      if (error.code === 'auth/operation-not-allowed') {
        const hostname = window.location.hostname;
        const projectId = "gen-lang-client-0231068133";
        throw new Error(`Google Sign-in is not enabled or this domain (${hostname}) is not authorized in your Firebase Console. 
Please:
1. Go to https://console.firebase.google.com/project/${projectId}/authentication/settings
2. Add "${hostname}" to "Authorized domains".
3. Go to https://console.firebase.google.com/project/${projectId}/authentication/providers and enable Google.`);
      }
      if (error.code === 'auth/network-request-failed') {
        throw new Error("Network error during login. This usually occurs if the domain is not authorized in Firebase Console or if popups are blocked. Please try opening the app in a new tab.");
      }
      throw error;
    }
  };

  const signInWithApple = async () => {
    const provider = new OAuthProvider('apple.com');
    return await signInWithPopup(auth, provider);
  };

  const signInWithEmail = async (email: string, pass: string) => {
    await signInWithEmailAndPassword(auth, email, pass);
  };

  const signUpWithEmail = async (email: string, pass: string, name: string) => {
    const { user: newUser } = await createUserWithEmailAndPassword(auth, email, pass);
    await updateProfile(newUser, { displayName: name });
  };

  const setupRecaptcha = (containerId: string) => {
    try {
      const container = document.getElementById(containerId);
      if (!container) {
        console.warn(`Recaptcha container #${containerId} not found yet.`);
        return;
      }
      
      // If we already have a verifier, check if it's still attached to the same container
      // and if the container still exists in the DOM.
      if (recaptchaVerifier) {
        try {
          // If we can render it, it's still functional
          return;
        } catch (e) {
          console.warn("Existing verifier seems broken, re-initializing...");
          setRecaptchaVerifier(null);
        }
      }
      
      const verifier = new RecaptchaVerifier(auth, containerId, {
        size: 'invisible',
        callback: () => {
          console.log("Recaptcha resolved");
        },
        'expired-callback': () => {
          console.log("Recaptcha expired, resetting...");
          setRecaptchaVerifier(null);
        }
      });
      
      setRecaptchaVerifier(verifier);
    } catch (error: any) {
      console.error("Recaptcha initialization error:", error);
    }
  };

  const signInWithPhone = async (phoneNumber: string) => {
    const sanitizedPhone = phoneNumber.replace(/[\s\-\(\)]/g, '');
    
    if (!sanitizedPhone || sanitizedPhone.length < 8 || !sanitizedPhone.startsWith('+')) {
      throw new Error("Please enter a valid phone number with country code (e.g., +919876543210)");
    }
    
    // Always try to get a fresh verifier or use existing
    let verifier = recaptchaVerifier;
    
    if (!verifier) {
      const container = document.getElementById('recaptcha-container');
      if (!container) {
        throw new Error("Security check container missing. Please refresh the page.");
      }
      
      verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible'
      });
      setRecaptchaVerifier(verifier);
    }
    
    try {
      console.log("Attempting Phone Auth for:", sanitizedPhone);
      // Explicitly render
      await verifier.render();
      
      return await signInWithPhoneNumber(auth, sanitizedPhone, verifier);
    } catch (error: any) {
      console.error("Firebase Phone Auth Detailed Error:", error);
      
      // If there was an error, it's often good to clear the verifier so a fresh one can be tried
      try {
        if (verifier) {
          verifier.clear();
          setRecaptchaVerifier(null);
        }
      } catch (clearErr) {
        console.warn("Error clearing verifier after failure:", clearErr);
      }
      
      if (error.code === 'auth/operation-not-allowed') {
        const hostname = window.location.hostname;
        const projectId = "gen-lang-client-0231068133";
        throw new Error(`Authentication provider is not enabled or this domain (${hostname}) is not authorized in your Firebase Console. 
Please:
1. Go to https://console.firebase.google.com/project/${projectId}/authentication/settings
2. Add "${hostname}" to "Authorized domains".
3. Go to https://console.firebase.google.com/project/${projectId}/authentication/providers
4. Enable "Phone" in the 'Sign-in method' tab.`);
      }
      
      if (error.code === 'auth/internal-error') {
        throw new Error("Authentication service error. This often happens if the domain is not authorized in Firebase Console or if a reCAPTCHA error occurred. Please try using Google Sign-in if this persists.");
      }
      
      if (error.code === 'auth/invalid-phone-number') {
        throw new Error("The phone number provided is invalid. Please use +[country code][number] format.");
      }
      
      if (error.code === 'auth/too-many-requests') {
        throw new Error("Too many verification attempts for this number. Please try again later.");
      }
      
      if (error.code === 'auth/network-request-failed') {
        throw new Error("Network error. Please check your internet connection and ensure no ad-blockers are interfering with reCAPTCHA.");
      }
      
      throw new Error(error.message || "Failed to send verification code.");
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      userData,
      isAdmin, 
      isStaff, 
      staffInfo, 
      loading, 
      signInWithGoogle, 
      signInWithApple,
      signInWithEmail,
      signUpWithEmail,
      setupRecaptcha,
      signInWithPhone,
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

